import { create } from 'zustand'

import { setAccessTokenGetter } from '@/api/client'
import { setImportTokenGetter } from '@/api/supplierPortal'
import type { OrganizationType, Session } from '@/api/auth'

/**
 * Κατάσταση συνεδρίας.
 *
 * Το access token μένει αποκλειστικά στη μνήμη και ποτέ σε localStorage: ένα
 * XSS θα το διάβαζε από εκεί. Η επιβίωση μετά από ανανέωση σελίδας στηρίζεται
 * στο httpOnly refresh cookie, το οποίο η JavaScript δεν μπορεί να διαβάσει.
 */
interface AuthState {
  session: Session | null
  /** true μέχρι να ολοκληρωθεί η πρώτη απόπειρα ανανέωσης στο άνοιγμα. */
  isBootstrapping: boolean
  setSession: (session: Session | null) => void
  setBootstrapped: () => void
  hasPermission: (permission: string) => boolean
  /** Ο τύπος του ενεργού οργανισμού: καθορίζει ποια πλοήγηση βλέπει ο χρήστης. */
  organizationType: () => OrganizationType | null
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  isBootstrapping: true,
  setSession: (session) => set({ session }),
  setBootstrapped: () => set({ isBootstrapping: false }),
  hasPermission: (permission) => get().session?.permissions.includes(permission) ?? false,
  organizationType: () => {
    const session = get().session
    if (!session) return null
    return (
      session.memberships.find((m) => m.organizationId === session.organizationId)
        ?.organizationType ?? null
    )
  },
}))

// Ο HTTP client δεν εισάγει το store, για να μην υπάρξει κυκλική εξάρτηση.
setAccessTokenGetter(() => useAuthStore.getState().session?.accessToken ?? null)
setImportTokenGetter(() => useAuthStore.getState().session?.accessToken ?? null)
